import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Stat, Note, NumField, CalcButton, BrangusSwitch, useResultsFocus, fmt, num, val, str } from "./ToolUI";

const DAMS = [
  ["brahman", "Vaca Brahman", 1],
  ["nelore", "Vaca Nelore", 1],
  ["bxn", "Vaca cruza Brahman × Nelore", 1],
  ["f1", "Vaca F1 cebú × Angus", 0.5],
  ["brangus", "Vaca Brangus", 0.375],
];
const SIRES = [
  ["brangus", "Toro / semen Brangus (3/8 cebú · 5/8 Angus)", 0.375],
  ["ultra", "Toro / semen Ultrablack o Brangus alto Angus (1/4 cebú)", 0.25],
];

const perf = (p, ind, het) => {
  const tau = 1 - ind;
  const factor = 1 + (p.taurus_weaning_pct / 100) * tau + (num(p.heterosis_pct) / 100) * het;
  return { weaning: val(p.weaning_base) * factor, final: val(p.final_base) * factor, months: Math.max(val(p.months_base) - p.precocity_months * tau, 0) };
};

const Pick = ({ id, label, value, onChange, options }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-[#5C635E]">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-white" data-testid={`${id}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${id}-option-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const compute = (params, { dam, sire, v }) => {
  const p = { ...params, ...v };
  const d = DAMS.find((x) => x[0] === dam)[2];
  const s = SIRES.find((x) => x[0] === sire)[2];
  const pureHet = 2 * d * (1 - d);
  return { d, calfInd: (d + s) / 2, het: s * (1 - d) + (1 - s) * d, pureHet, base: perf(p, d, pureHet), calf: perf(p, (d + s) / 2, s * (1 - d) + (1 - s) * d) };
};

const Bar = ({ ind }) => (
  <div className="flex h-10 overflow-hidden text-xs font-semibold text-white">
    <div className="flex items-center justify-center bg-[#C5A059] transition-all duration-500" style={{ width: `${ind * 100}%` }}>Cebú {fmt(ind * 100, 1)}%</div>
    {ind < 1 && <div className="flex items-center justify-center bg-[#1D3324] transition-all duration-500" style={{ width: `${(1 - ind) * 100}%` }}>Angus {fmt((1 - ind) * 100, 1)}%</div>}
  </div>
);

const diff = (v, d, sign) => (Number.isFinite(v) && Math.abs(v) >= 0.05 ? `${sign}${fmt(Math.abs(v), d)}` : fmt(v === 0 ? 0 : v, d));

const Table = ({ r, brangus }) => {
  const rows = [["Peso al destete (kg)", fmt(r.base.weaning), fmt(r.calf.weaning), diff(r.calf.weaning - r.base.weaning, 0, "+")],
    ["Peso final (kg)", fmt(r.base.final), fmt(r.calf.final), diff(r.calf.final - r.base.final, 0, "+")],
    ["Edad a terminación (meses)", fmt(r.base.months, 1), fmt(r.calf.months, 1), diff(r.base.months - r.calf.months, 1, "−")],
    ["Vigor híbrido retenido", `${fmt(r.pureHet * 100)}%`, `${fmt(r.het * 100)}%`, ""]];
  const cols = brangus ? 4 : 2;
  return (
    <div className={`grid ${brangus ? "grid-cols-4" : "grid-cols-2"} gap-px bg-[#8B2B22]/10 text-xs sm:text-sm`} data-testid="cross-results">
      {[["", "Sin Brangus", "Con Brangus", "Diferencia"], ...rows].map((row, i) => row.slice(0, cols).map((c, j) => (
        <div key={`${i}-${j}`} className={`p-2 sm:p-3 break-words hyphens-auto ${i === 0 ? "bg-[#1D3324] text-stone-100 font-semibold" : j >= 2 ? "bg-[#F3EAD7] font-semibold text-[#8B2B22]" : "bg-white"} ${j ? "text-right" : "text-[#5C635E]"}`}>{c}</div>
      )))}
    </div>
  );
};

export const CrossTool = ({ params }) => {
  const [f, setF] = useState(() => ({ dam: "brahman", sire: "brangus", v: { weaning_base: str(params.weaning_base), final_base: str(params.final_base), months_base: str(params.months_base), heterosis_pct: str(params.heterosis_pct) } }));
  const [brangus, setBrangus] = useState(true);
  const r = compute(params, f);
  const { ref, flash, go } = useResultsFocus();
  const upd = (patch) => setF((p) => ({ ...p, ...patch }));
  const set = (k) => (x) => upd({ v: { ...f.v, [k]: x } });

  return (
    <div className="grid gap-10 lg:grid-cols-12" data-testid="tool-cross">
      <form onSubmit={go} className="lg:col-span-5 space-y-5">
        <Pick id="cross-dam" label="Vientre (madre)" value={f.dam} onChange={(dam) => upd({ dam })} options={DAMS} />
        <div>
          <p className="text-xs uppercase tracking-wider text-[#8B2B22] mb-3">Datos de su rodeo actual (cría pura del mismo vientre)</p>
          <div className="grid grid-cols-2 gap-4">
            <NumField id="cross-weaning-base" label="Peso al destete" suffix="kg" value={f.v.weaning_base} onChange={set("weaning_base")} />
            <NumField id="cross-final-base" label="Peso final" suffix="kg" value={f.v.final_base} onChange={set("final_base")} />
            <div className="col-span-2"><NumField id="cross-months-base" label="Edad a terminación" suffix="meses" value={f.v.months_base} onChange={set("months_base")} /></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <BrangusSwitch id="cross-brangus" checked={brangus} onChange={setBrangus} />
          {brangus && <div className="col-span-2"><Pick id="cross-sire" label="Padre Brangus" value={f.sire} onChange={(sire) => upd({ sire })} options={SIRES} /></div>}
          {brangus && <div className="col-span-2"><NumField id="cross-heterosis-pct" label="Heterosis cebú × Angus" suffix="%" value={f.v.heterosis_pct} onChange={set("heterosis_pct")} /></div>}
          <CalcButton id="cross" />
        </div>
      </form>
      <div ref={ref} key={flash} className={`lg:col-span-7 space-y-6 scroll-mt-28 ${flash ? "animate-in fade-in zoom-in-95 duration-500" : ""}`} data-testid="cross-results-panel">
        <div data-testid="cross-composition">
          <p className="text-xs text-[#5C635E] mb-2">Composición racial de la cría {brangus ? "con Brangus" : "actual"}</p>
          <Bar ind={brangus ? r.calfInd : r.d} />
        </div>
        <Table r={r} brangus={brangus} />
        {brangus ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Stat accent label="Más kilos al destete" value={fmt(r.calf.weaning - r.base.weaning)} unit="kg por cría" testid="cross-weaning" />
            <Stat label="Más kilos a venta" value={fmt(r.calf.final - r.base.final)} unit="kg por cría" testid="cross-final" />
            <Stat label="Terminación más temprana" value={fmt(r.base.months - r.calf.months, 1)} unit="meses antes" testid="cross-months" />
            <Stat label="Vigor híbrido retenido" value={fmt(r.het * 100)} unit="%" testid="cross-heterosis" />
          </div>
        ) : (
          <p className="bg-[#F3EAD7]/70 p-4 text-sm text-[#1D3324]" data-testid="cross-brangus-off">Active «Incorporar genética Brangus» para ver la cría mejorada.</p>
        )}
        <Note>«Sin Brangus» es una cría del mismo vientre con un padre de su misma raza. Valores de referencia generales: el resultado real depende de la genética del toro, la nutrición y el manejo.</Note>
      </div>
    </div>
  );
};
